import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import {
    Activity, Heart, Droplet, Scale, Thermometer, Brain, Stethoscope, ShieldCheck, Wind, Video, Eye,
    Flame, Dumbbell, Droplets, AlertTriangle, Bone, Zap, FileText,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function Solutions() {
    const { t } = useTranslation();
    const location = useLocation();

    useEffect(() => {
        if (location.hash === "#vision") {
            const el = document.getElementById("vision");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [location.hash]);

    // The 12 BIGGEST vital readings on every kiosk assessment — matched to the
    // physical kiosk receipt layout.  `big: true` -> highlighted with orange ring.
    const SOLUTIONS = [
        { icon: Heart, key: "bp", big: true },
        { icon: Activity, key: "hr", big: true },
        { icon: Droplet, key: "spo2", big: true },
        { icon: Thermometer, key: "temp", big: true },
        { icon: Scale, key: "bmi", big: true },
        { icon: Flame, key: "bodyFat", big: true },
        { icon: Dumbbell, key: "muscle", big: true },
        { icon: Droplets, key: "water", big: true },
        { icon: AlertTriangle, key: "visceralFat", big: true },
        { icon: Bone, key: "bone", big: true },
        { icon: Eye, key: "vision", big: true },
        { icon: Zap, key: "metabolism", big: true },
        // Secondary platform capabilities
        { icon: Brain, key: "body" },
        { icon: Wind, key: "resp" },
        { icon: Thermometer, key: "facial" },
        { icon: Video, key: "telemedicine" },
        { icon: FileText, key: "file" },
        { icon: ShieldCheck, key: "encrypted" },
    ];
    return (
        <div className="bg-white" data-testid="solutions-page">
            <Navbar />
            <main className="pt-28">
                <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-4">{t("solutions.eyebrow")}</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter max-w-4xl mb-6">
                        {t("solutions.titleLine1")}<br />
                        <span className="brand-gradient-text">{t("solutions.titleLine2")}</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
                        {t("solutions.intro")}
                    </p>
                </section>

                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-10">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-[var(--medx-red)] border border-orange-200 text-xs font-mono tracking-[0.2em]">
                            ★ 12 KEY VITALS
                        </span>
                        <div className="text-xs text-gray-500">{t("solutions.keyVitalsLabel")}</div>
                    </div>
                </section>

                <section className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
                    {SOLUTIONS.map((s, i) => {
                        const Icon = s.icon;
                        const isHero = !!s.big;
                        return (
                            <div
                                key={i}
                                id={s.key === "vision" ? "vision" : undefined}
                                className={`relative card-soft p-6 group ${isHero ? "ring-2 ring-[var(--medx-orange)]/35 shadow-[0_15px_50px_-30px_rgba(247,127,0,0.4)]" : ""}`}
                                data-testid={`solution-${i}`}
                            >
                                {isHero && (
                                    <span className="absolute top-4 right-4 text-[9px] font-mono tracking-[0.22em] text-[var(--medx-red)] bg-white border border-[var(--medx-orange)]/40 px-2 py-1 rounded-full">
                                        KEY VITAL
                                    </span>
                                )}
                                <div className={`w-12 h-12 rounded-xl grid place-items-center mb-4 transition-colors ${isHero ? "bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] text-white shadow-md" : "bg-orange-50 text-[var(--medx-red)] group-hover:bg-[var(--medx-red)] group-hover:text-white"}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="font-display text-xl font-semibold mb-2">{t(`solutions.items.${s.key}.title`)}</div>
                                <div className="text-sm text-gray-600 leading-relaxed">{t(`solutions.items.${s.key}.desc`)}</div>
                            </div>
                        );
                    })}
                </section>

                <section className="bg-[#0d0d10] text-white grain relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(247,127,0,0.18),transparent_60%)]" />
                    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-orange)] mb-3">{t("solutions.remoteEyebrow")}</div>
                            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-5 text-white">
                                {t("solutions.remoteTitle")}
                            </h2>
                            <p className="text-white/85 leading-relaxed mb-6">
                                {t("solutions.remoteP1Pre")}<strong className="text-white">{t("solutions.remoteP1Bold")}</strong>
                            </p>
                            <ul className="space-y-3 text-sm text-white/85">
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.remoteBullets.b1")}</li>
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.remoteBullets.b2")}</li>
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.remoteBullets.b3")}</li>
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.remoteBullets.b4")}</li>
                            </ul>
                        </div>
                        <div className="rounded-3xl overflow-hidden">
                            <img src="https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/vd9c19k2_medx12.png" alt="Remote doctor consultation" className="w-full aspect-[4/3] object-cover" />
                        </div>
                    </div>
                </section>

                <section className="bg-[#0d0d10] text-white grain relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(247,127,0,0.18),transparent_60%)]" />
                    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-orange)] mb-3">{t("solutions.outputEyebrow")}</div>
                            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-5 text-white">{t("solutions.outputTitle")}</h2>
                            <p className="text-white leading-relaxed mb-6">
                                {t("solutions.outputP1")}
                            </p>
                            <ul className="space-y-3 text-sm text-white">
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.outputBullets.b1")}</li>
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.outputBullets.b2")}</li>
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.outputBullets.b3")}</li>
                                <li className="flex gap-3"><span className="text-[var(--medx-orange)]">→</span> {t("solutions.outputBullets.b4")}</li>
                            </ul>
                        </div>
                        <div className="rounded-3xl overflow-hidden">
                            <img src="https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/ck1mc1yk_med2.png" alt="Medx AI Kiosk" className="w-full aspect-[4/3] object-cover bg-white" />
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
